<script setup>
import { useDebounceFn } from '@vueuse/core'

const props = defineProps({
  mode: {
    type: String,
    default: 'event',
    validator: (value) => ['event', 'model'].includes(value)
  },
  parentIsLoading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['sharePermissionChanged'])

const modelChecked = defineModel('checked', { type: Boolean, default: false })

const internalAgreedToShare = ref(false)
const internalIsLoading = ref(false)

const user = useUser()
const tech = useTech()
const theme = inject('theme') || tech.lowestLevelTheme
const checkboxId = 'shareAchievementCheckboxUniqueId'

const currentCheckboxValue = computed({
  get () {
    return props.mode === 'model' ? modelChecked.value : internalAgreedToShare.value
  },
  set (newValue) {
    if (props.mode === 'model') {
      modelChecked.value = !!newValue
    } else {
      internalAgreedToShare.value = !!newValue
    }
  }
})

const displayIsLoading = computed(() => {
  return props.mode === 'model' ? props.parentIsLoading : internalIsLoading.value
})

const handleSharePermissionChange = async (agreed) => {
  if (agreed === user.data?.linkedin_achievement_share_permission) return

  internalIsLoading.value = true
  try {
    await user.update({
      ...user.data,
      linkedin_achievement_share_permission: agreed
    })
    emit('sharePermissionChanged', agreed)
  } catch (e) {
    console.error(e)
  } finally {
    internalIsLoading.value = false
  }
}

const debouncedHandleSharePermissionChange = useDebounceFn(handleSharePermissionChange, 750)

watch(internalAgreedToShare, (newValue) => {
  if (props.mode === 'event') {
    debouncedHandleSharePermissionChange(newValue)
  }
})

onMounted(() => {
  currentCheckboxValue.value = user.data?.linkedin_achievement_share_permission
})
</script>

<template>
  <div class="flex items-center">
    <AppCheckbox
      :id="checkboxId"
      v-model="currentCheckboxValue"
      :disabled="displayIsLoading"
      :checked-class="`${theme?.bgColor || 'bg-azure-radiance'} border ${theme?.borderColor || 'border-azure-radiance'}`"
      class="cursor-pointer">
      <span class="ml-2 cursor-pointer text-sm text-inherit">
        <slot>
          I give my permission for Certificates.dev to share my achievement on LinkedIn for me
        </slot>
      </span>
    </AppCheckbox>
    <div
      v-if="displayIsLoading"
      class="ml-3 flex items-center justify-center"
      :class="theme?.textColor || 'text-azure-radiance'">
      <LoadingSpinner class="!size-4" />
    </div>
  </div>
</template>
